import type { MouseEventHandler } from 'react';
import React from 'react';
import { Dock } from 'react-dock';
import ReactDOM from 'react-dom';

import { SchemaContext } from './../../ricos-context/index.js';
import type { RicosSchema } from './../../ricos-orchestrator/ricos-schema.js';
import { encodeUnicodeBase64, legacyPluginToNextMap } from './../../ricos-schema/next-schema/index.js';
import { getOverrideValue, hasWixInsiderOverride } from './../lib/overrides.js';

import { Tab, TabList, TabPanel, Tabs } from './components/Tabs.js';
import { RcpLogo } from './logo.js';
import { RichContentTree } from './tabs/rich-content-tree.js';
import { RicosContentEditorTab } from './tabs/ricos-content-editor/ricos-content-editor.js';
import { RicosNetworkConditionsTab } from './tabs/ricos-network-conditions.js';
import { RicosPropsTab } from './tabs/ricos-props.js';
import { RicosRenderingTab } from './tabs/ricos-rendering.js';
import { RicosSchemaTab } from './tabs/ricos-schema.js';
import { mainTheme as theme } from './theme.js';

type Props = {
  contentState: any;
  ricosProps: { plugins?: { type: string }[]; content?: any };
  mode: 'viewer' | 'editor';
  version: string;
  schema?: Pick<RicosSchema, 'getContent' | 'setContent'>;
};

const RicosDevTools: React.FC<Props & { autoOpen: boolean }> = ({
  contentState,
  ricosProps,
  mode,
  version,
  autoOpen,
  schema,
}) => {
  if (typeof window === 'undefined') {
    return null;
  }
  const [isOpen, toggle] = useGlobalDevToolsState(autoOpen);

  return (
    <>
      <button
        onClick={toggle}
        type="button"
        style={{
          border: 'none',
          background: 'transparent',
          position: 'absolute',
          right: '0px',
          color: 'black',
          padding: '5px',
          cursor: 'pointer',
          zIndex: 1,
        }}
        className="ricos-dev-tools-button"
        tabIndex={-1}
      >
        <RcpLogo fill={hasWixInsiderOverride ? '#F63B1F' : '#231f20'} />
      </button>
      {isOpen && (
        <RicosDevToolsDock
          contentState={contentState}
          ricosProps={ricosProps}
          close={toggle}
          mode={mode}
          version={version}
          schema={schema}
        />
      )}
    </>
  );
};

const DEV_TOOLS_KEY = '__RICOS_DEV_TOOLS_OPENED_ELEMENT__';

// We want all instances of this dashboard to be closed when opening current instance, so we use
// global state to track opened dashboard
const useGlobalDevToolsState = (initiallyOpen: boolean): [boolean, () => void] => {
  (window as any)[DEV_TOOLS_KEY] =
    (window as any)[DEV_TOOLS_KEY] ??
    (() => {
      const state = {
        activeDashboard: {} as object | undefined,
        subscribers: [] as (() => void)[],
        toggle: (identityObject: object) => {
          if (state.activeDashboard === identityObject) {
            state.activeDashboard = undefined;
          } else {
            state.activeDashboard = identityObject;
          }
          state.subscribers.forEach(x => x());
        },
        setActiveDashboard: (identityObject: object) => {
          state.activeDashboard = identityObject;
          state.subscribers.forEach(x => x());
        },
        subscribe: (fn: () => void) => {
          state.subscribers.push(fn);
        },
        unsubscribe: (fn: () => void) => {
          state.subscribers = state.subscribers.filter(x => x !== fn);
        },
      };
      return state;
    })();

  const [identityObject] = React.useState({});
  const [_, forceUpdate] = React.useReducer(x => x + 1, 0);

  React.useEffect(() => {
    (window as any)[DEV_TOOLS_KEY].subscribe(forceUpdate);
    if (initiallyOpen) {
      (window as any)[DEV_TOOLS_KEY].setActiveDashboard(identityObject);
    }
    return () => {
      (window as any)[DEV_TOOLS_KEY].unsubscribe(forceUpdate);
    };
  }, []);
  return [
    (window as any)[DEV_TOOLS_KEY].activeDashboard === identityObject,
    () => (window as any)[DEV_TOOLS_KEY].toggle(identityObject),
  ];
};

const RicosDevToolsDock: React.FC<Props & { close: () => void }> = ({
  contentState,
  close,
  ricosProps,
  mode,
  version,
  schema: schemaFallback,
}) => {
  const [tabIndex, setTabIndex] = React.useState('state');
  const encodedContent = encodeUnicodeBase64(JSON.stringify(contentState));
  const plugins = ricosProps.plugins?.map(p => legacyPluginToNextMap[p.type]) || [];
  const schema = React.useContext(SchemaContext) ?? schemaFallback;
  return ReactDOM.createPortal(
    <Dock
      position="bottom"
      dimMode="none"
      isVisible
      defaultSize={0.3}
      onSizeChange={() => {}}
      zIndex={10000}
      fluid
      duration={0}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          paddingTop: '15px',
          background: theme.mainBg,
          fontFamily: 'Helvetica Neue, Calibri Light, Roboto, sans-serif',
          fontSize: '13px',
        }}
      >
        <CloseButton onClick={close}>×</CloseButton>

        <div
          style={{
            position: 'absolute',
            right: 20,
            top: 2,
            color: theme.white60,
            fontSize: '14px',
            padding: '4px',
          }}
        >
          {mode === 'editor' ? `ricos-editor@${version}` : `ricos-viewer@${version}`}
          {hasWixInsiderOverride && (
            <>
              {' '}
              <span style={{ color: 'red' }}>{getOverrideValue()}</span>
            </>
          )}{' '}
          <a
            style={{ color: theme.white60, padding: '0 10px' }}
            href={`https://ricos.dev?v=${version}&plugins=${
              plugins.length ? plugins.join('.') : 'NONE'
            }#content=${encodedContent}`}
          >
            Open in RICOS.DEV
          </a>
        </div>
        <Tabs onSelect={setTabIndex} selectedIndex={tabIndex}>
          <TabList>
            <Tab index="state">State</Tab>
            <Tab index="contentEditor">Content Editor</Tab>
            <Tab index="props">All Props</Tab>
            <Tab index="schema">Schema</Tab>
            <Tab index="rendering">Rendering</Tab>
            {mode === 'editor' && <Tab index="networkConditions">Network conditions</Tab>}
          </TabList>

          <TabPanel>
            {({ index }): React.ReactNode => {
              switch (index) {
                case 'state':
                  return <RichContentTree content={contentState} />;
                case 'contentEditor':
                  return (
                    <RicosContentEditorTab
                      editorPlugins={plugins}
                      content={schema.getContent()}
                      onChange={content => schema.setContent(content)}
                      mode={mode}
                    />
                  );
                case 'props':
                  return <RicosPropsTab ricosProps={ricosProps} />;
                case 'schema':
                  return <RicosSchemaTab />;
                case 'rendering':
                  return <RicosRenderingTab />;
                case 'networkConditions':
                  return mode === 'editor' ? <RicosNetworkConditionsTab /> : null;
                default:
                  return <div>unknown tab</div>;
              }
            }}
          </TabPanel>
        </Tabs>
      </div>
    </Dock>,
    document.body
  );
};

const CloseButton: React.FC<{
  onClick: MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
}> = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: 'none',
      border: 'none',
      position: 'absolute',
      top: 2,
      right: 0,
      color: theme.white60,
      fontSize: '18px',
      cursor: 'pointer',
    }}
  >
    {children}
  </button>
);
export default RicosDevTools;
